// pipelineSources — pub/sub for "what is the AI actually analysing right now".
//
// Each AI generator (readiness, workout-gen, photo, meal-parse, meal-plan,
// post-workout, last-session) computes a short list of human labels that
// describe the *actual* signals/values feeding the model, then calls
// `publishSources(feature, sources)`. The AIAnalysingPipeline subscribes
// by feature key and replaces its hardcoded fallback rows with the live
// labels, so the user sees real numbers (HRV ms, kcal, ACWR, photo size,
// etc.) rather than a generic placeholder.

const listeners = new Map(); // feature -> Set<cb>
const current   = new Map(); // feature -> sources[]

export function publishSources(feature, sources) {
  if (!feature) return;
  const safe = Array.isArray(sources) ? sources.filter(Boolean) : [];
  if (!safe.length) { current.delete(feature); }
  else              { current.set(feature, safe); }
  const set = listeners.get(feature);
  if (set) set.forEach(cb => { try { cb(safe); } catch {} });
}

export function subscribeSources(feature, cb) {
  if (!feature) return () => {};
  if (!listeners.has(feature)) listeners.set(feature, new Set());
  const set = listeners.get(feature);
  set.add(cb);
  if (current.has(feature)) cb(current.get(feature));
  return () => {
    set.delete(cb);
    if (set.size === 0) current.delete(feature);
  };
}

// ─── label builders ───────────────────────────────────────────────────

const n0 = (v) => (v == null || !Number.isFinite(v)) ? null : v;
const r1 = (v) => v == null ? "—" : (Math.round(v * 10) / 10).toString();
const signed = (v) => v == null ? "" : (v > 0 ? `+${v}` : `${v}`);

export function readinessSources(inputs) {
  if (!inputs) return [];
  const out = [];

  const s = inputs.sleep;
  if (s) {
    const h = n0(s.totalHours);
    const a = n0(s.avg7dHours);
    if (h != null) out.push(`Sleep · ${r1(h)}h${a != null ? ` (7d ${r1(a)}h)` : ""}`);
  }

  const hrv = inputs.hrv;
  if (hrv && n0(hrv.today) != null) {
    out.push(`HRV · ${Math.round(hrv.today)}ms${n0(hrv.deltaPct) != null ? ` (${signed(Math.round(hrv.deltaPct))}%)` : ""}`);
  }

  const rhr = inputs.rhr;
  if (rhr && n0(rhr.today) != null) {
    out.push(`RHR · ${Math.round(rhr.today)} bpm${n0(rhr.deltaBpm) != null ? ` (${signed(Math.round(rhr.deltaBpm))})` : ""}`);
  }

  const tr = inputs.training;
  if (tr) {
    const bits = [];
    if (n0(tr.hoursSinceLastSession) != null) bits.push(`${Math.round(tr.hoursSinceLastSession)}h since last`);
    if (n0(tr.acwr) != null)                   bits.push(`ACWR ${tr.acwr.toFixed(2)}`);
    if (bits.length) out.push(`Workouts · ${bits.join(" · ")}`);
  }

  const act = inputs.activity;
  if (act && n0(act.steps48h) != null) {
    out.push(`Steps · ${Math.round(act.steps48h).toLocaleString()} (48h)`);
  }

  const nut = inputs.nutrition;
  if (nut && (n0(nut.todayCalories) != null || n0(nut.todayProtein) != null)) {
    const k = nut.todayCalories != null ? `${Math.round(nut.todayCalories)} kcal` : "";
    const p = nut.todayProtein  != null ? `${Math.round(nut.todayProtein)}g P`     : "";
    out.push(`Nutrition · ${[k, p].filter(Boolean).join(" · ")}`);
  }

  if (inputs.mounjaro?.dayOfCycle != null) out.push(`Mounjaro · day ${inputs.mounjaro.dayOfCycle}/7`);
  if (inputs.fasting?.enabled) {
    out.push(`Fasting · ${inputs.fasting.protocol || "16:8"}${inputs.fasting.currentlyFasted ? " · active" : ""}`);
  }
  if (inputs.ketone?.mmol != null) out.push(`Ketone · ${r1(inputs.ketone.mmol)} mmol`);

  const rec = inputs.recovery;
  if (rec) {
    if (n0(rec.wristTempDeltaC) != null && Math.abs(rec.wristTempDeltaC) >= 0.15) {
      out.push(`Wrist temp · ${rec.wristTempDeltaC > 0 ? "+" : ""}${r1(rec.wristTempDeltaC)}°C vs 14d`);
    }
    if (n0(rec.vo2Max) != null) {
      const d = n0(rec.vo2MaxDelta30d);
      out.push(`VO2max · ${r1(rec.vo2Max)} mL/kg·min${d != null ? ` (${signed(+d.toFixed(1))} 30d)` : ""}`);
    }
    if (n0(rec.lastWorkoutHrRecoveryBpm) != null) {
      out.push(`HR recovery · ${Math.round(rec.lastWorkoutHrRecoveryBpm)} bpm/min`);
    }
    if (n0(rec.mindfulMinutesToday) != null && rec.mindfulMinutesToday > 0) {
      out.push(`Mindful · ${Math.round(rec.mindfulMinutesToday)} min today`);
    }
  }
  if (n0(inputs.activity?.walkingHrAvgBpm) != null) {
    out.push(`Walking HR · ${Math.round(inputs.activity.walkingHrAvgBpm)} bpm`);
  }

  return out.length ? out : ["Signals", "History"];
}

export function workoutGenSources({ readiness, goal, duration, equip, acwr, lastWorkout, muscleRecency } = {}) {
  const out = [];
  if (readiness) out.push(`Readiness · ${readiness}`);
  if (goal)      out.push(`Goal · ${goal}`);
  if (duration)  out.push(`Duration · ${duration}m`);
  if (equip)     out.push(`Equipment · ${equip}`);
  if (n0(acwr) != null) out.push(`ACWR · ${acwr.toFixed(2)}`);
  if (lastWorkout?.name) {
    const hAgo = n0(lastWorkout.hoursAgo);
    out.push(`Last · ${lastWorkout.name}${hAgo != null ? ` (${Math.round(hAgo)}h ago)` : ""}`);
  }
  if (muscleRecency && typeof muscleRecency === "object") {
    const trained = Object.keys(muscleRecency).length;
    if (trained) out.push(`Muscle recency · ${trained} groups tracked`);
  }
  return out.length ? out : ["Readiness", "Goal", "Equipment"];
}

export function mobilitySources({ readiness, lastWorkout, focusAreas, duration, hrv, sleepHours } = {}) {
  const out = [];
  if (readiness) out.push(`Readiness · ${readiness}`);
  if (hrv && n0(hrv.today) != null) {
    out.push(`HRV · ${Math.round(hrv.today)}ms${n0(hrv.deltaPct) != null ? ` (${signed(Math.round(hrv.deltaPct))}%)` : ""}`);
  }
  if (n0(sleepHours) != null) out.push(`Sleep · ${r1(sleepHours)}h`);
  if (lastWorkout?.name) {
    const dAgo = n0(lastWorkout.daysAgo);
    out.push(`Last · ${lastWorkout.name}${dAgo != null ? ` (${Math.round(dAgo)}d ago)` : ""}`);
  }
  if (Array.isArray(focusAreas) && focusAreas.length) {
    out.push(`Focus · ${focusAreas.slice(0, 4).join(", ")}`);
  }
  if (n0(duration) != null) out.push(`Target duration · ${duration}m`);
  out.push("Joint-by-joint flow");
  out.push("Cues + breath + form");
  return out.length ? out : ["Readiness", "Last workout", "Mobility library"];
}

export function postWorkoutSources(ctx) {
  if (!ctx) return ["Session", "History"];
  const out = [];
  const sess = ctx.session;
  if (sess) {
    const ex = Array.isArray(sess.exercises) ? sess.exercises.length : 0;
    const sets = Array.isArray(sess.exercises)
      ? sess.exercises.reduce((s, e) => s + (Array.isArray(e.sets) ? e.sets.length : 0), 0)
      : 0;
    out.push(`Session · ${ex} exercises · ${sets} sets`);
    if (n0(sess.totalVolumeKg) != null) out.push(`Volume · ${Math.round(sess.totalVolumeKg).toLocaleString()} kg`);
    if (n0(sess.durationMin) != null)   out.push(`Duration · ${Math.round(sess.durationMin)}m`);
    if (n0(sess.maxHr) != null)         out.push(`Max HR · ${Math.round(sess.maxHr)} bpm`);
    const hrr = n0(sess.watch?.hrRecoveryBpm);
    if (hrr != null && hrr > 0) out.push(`HR recovery · ${Math.round(hrr)} bpm/min`);
    if (n0(sess.prCount) != null && sess.prCount > 0) out.push(`PRs · ${sess.prCount}`);
  }
  if (Array.isArray(ctx.priorSessions) && ctx.priorSessions.length) {
    out.push(`Prior sessions · ${ctx.priorSessions.length}`);
  }
  if (ctx.sleepLastNight?.hours != null) out.push(`Sleep · ${r1(ctx.sleepLastNight.hours)}h`);
  if (ctx.todayNutrition?.calories != null) {
    out.push(`Today's nutrition · ${Math.round(ctx.todayNutrition.calories)} kcal`);
  }
  return out.length ? out : ["Session", "History"];
}

export function photoSources({ photoBytes, hint, region, halalOnly } = {}) {
  const out = [];
  if (n0(photoBytes) != null) out.push(`Photo · ${Math.round(photoBytes / 1024)} KB`);
  if (hint) out.push(`Hint · "${String(hint).slice(0, 32)}${hint.length > 32 ? "…" : ""}"`);
  out.push("Vision model");
  out.push(`Region · ${region || "SA"} brand DB`);
  if (halalOnly) out.push("Halal filter");
  out.push("Portion · macro split");
  return out;
}

export function mealParseSources({ text, region, halalOnly } = {}) {
  const out = [];
  if (text) out.push(`Text · "${String(text).slice(0, 36)}${text.length > 36 ? "…" : ""}"`);
  out.push(`Region · ${region || "SA"} food DB`);
  if (halalOnly) out.push("Halal filter");
  out.push("Macro estimator");
  return out;
}

export function bodyCompositionSources(snapshot) {
  if (!snapshot) return ["Body composition", "Trend window"];
  const out = [];
  const c = snapshot.current || {};
  const w = snapshot.period_window;
  const bf = snapshot.body_fat_window;

  if (n0(c.weight_kg)    != null) out.push(`Weight · ${r1(c.weight_kg)} kg`);
  if (n0(c.body_fat_pct) != null) out.push(`Body fat · ${r1(c.body_fat_pct)}%`);
  if (n0(c.lean_mass_kg) != null) out.push(`Lean mass · ${r1(c.lean_mass_kg)} kg`);
  if (n0(c.bmi)          != null) out.push(`BMI · ${r1(c.bmi)}${c.bmi_band ? " · " + c.bmi_band : ""}`);

  if (w) {
    out.push(`Weight ${snapshot.period_days}d · ${signed(+w.weight_delta_kg.toFixed(1))} kg (${snapshot.sample_count} samples)`);
  }
  if (bf) {
    out.push(`Body fat ${snapshot.period_days}d · ${signed(+bf.delta_pct.toFixed(1))}%`);
  }
  if (n0(snapshot.lean_delta_kg) != null) out.push(`Lean ${snapshot.period_days}d · ${signed(+snapshot.lean_delta_kg.toFixed(1))} kg`);
  if (n0(snapshot.fat_delta_kg)  != null) out.push(`Fat ${snapshot.period_days}d · ${signed(+snapshot.fat_delta_kg.toFixed(1))} kg`);
  if (n0(snapshot.distance_to_goal_kg) != null) {
    out.push(`To goal · ${signed(+snapshot.distance_to_goal_kg.toFixed(1))} kg`);
  }
  const fit = snapshot.fitness;
  if (fit) {
    if (n0(fit.vo2_max_ml_kg_min) != null) {
      const d = n0(fit.vo2_max_delta_30d);
      out.push(`VO2max · ${r1(fit.vo2_max_ml_kg_min)} mL/kg·min${d != null ? ` (${signed(+d.toFixed(1))} 30d)` : ""}`);
    }
    if (n0(fit.wrist_temp_delta_c_today) != null && Math.abs(fit.wrist_temp_delta_c_today) >= 0.15) {
      out.push(`Wrist temp · ${fit.wrist_temp_delta_c_today > 0 ? "+" : ""}${r1(fit.wrist_temp_delta_c_today)}°C vs baseline`);
    }
  }

  return out.length ? out : ["Body composition", "Trend window"];
}

export function calorieTargetSources(c, lossPerWeek) {
  if (!c) return ["Intake history", "Weight trend", "Measured burn"];
  const k = (v) => Math.round(v).toLocaleString();
  const out = [];
  if (n0(c.current_weight_kg) != null) {
    out.push(`Weight · ${r1(c.current_weight_kg)} kg${n0(c.body_fat_pct) != null ? ` · ${r1(c.body_fat_pct)}% BF` : ""}`);
  }
  if (n0(c.avg_intake_kcal) != null) {
    out.push(`Intake avg · ${k(c.avg_intake_kcal)} kcal · ${c.intake_days}d`);
  }
  if (n0(c.observed_weekly_rate_kg) != null) {
    out.push(`Weight trend · ${signed(c.observed_weekly_rate_kg)} kg/wk · ${c.span_days}d`);
  }
  if (n0(c.tdeeBalance) != null) {
    out.push(`Energy-balance TDEE · ${k(c.tdeeBalance)} kcal`);
  }
  if (n0(c.tdeeActivity) != null) {
    out.push(`Measured burn · ${k(c.tdeeActivity)} kcal · ${c.burn_days}d`);
  }
  out.push(`Reconciled TDEE · ${k(c.empiricalTDEE)} kcal · ${Math.round((c.weight_trend_confidence || 0) * 100)}% trend`);
  if (n0(c.daily_deficit) != null) {
    out.push(`Deficit · ${c.daily_deficit} kcal/day · ${lossPerWeek} kg/wk`);
  }
  out.push(`Target · ${k(c.requestedTarget)} kcal/day`);
  if (n0(c.training_burn_kcal) != null && c.training_burn_kcal > 0) {
    out.push(`Training day · +${k(c.training_burn_kcal)} kcal (${c.training_days_seen} workouts)`);
  }
  return out.length ? out : ["Intake history", "Weight trend"];
}

export function mealPlanSources(payload) {
  if (!payload) return ["History", "Macros", "Window"];
  const out = [];
  if (payload.current_time) {
    out.push(`Now · ${payload.current_time}`);
  }
  const c = payload.consumed_today;
  if (c) {
    const eaten = c.items?.length || 0;
    out.push(`Already eaten · ${Math.round(c.kcal)} kcal · ${Math.round(c.protein_g)}g P (${eaten} item${eaten === 1 ? "" : "s"})`);
  }
  const r = payload.remaining_target;
  if (r) {
    out.push(`Remaining today · ${Math.round(r.kcal)} kcal · ${Math.round(r.protein_g)}g P`);
  } else {
    const m = payload.macro_target;
    if (m) out.push(`Target · ${Math.round(m.kcal)} kcal · ${Math.round(m.protein_g)}g P`);
  }
  const win = payload.eating_window;
  if (win?.first_meal_avg && win?.last_meal_avg) {
    out.push(`Window · ${win.first_meal_avg}–${win.last_meal_avg}${win.days_analyzed ? ` (${win.days_analyzed}d)` : ""}`);
  }
  const tf = payload.top_foods;
  if (Array.isArray(tf)) out.push(`Top foods · ${tf.length} (last 90d)`);
  if (payload.training_day != null) {
    out.push(`Training day · ${payload.training_day ? "yes" : "no"}`);
  }
  return out.length ? out : ["History", "Macros"];
}
